import { useEffect } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { assetUrl } from "@/lib/constants";

export const ImageLightbox = ({ images, index, title, onClose, onIndexChange }) => {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (images.length > 1 && e.key === "ArrowLeft") onIndexChange((index - 1 + images.length) % images.length);
      if (images.length > 1 && e.key === "ArrowRight") onIndexChange((index + 1) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [index, images.length, onClose, onIndexChange]);

  if (!images.length) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[95] flex items-center justify-center bg-black/90 p-6"
      onClick={onClose}
      data-testid="image-lightbox"
    >
      <button
        onClick={onClose}
        aria-label="Close image view"
        data-testid="lightbox-close-button"
        className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:border-[#E74C3C] hover:text-[#E74C3C]"
      >
        <X size={17} />
      </button>

      {images.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onIndexChange((index - 1 + images.length) % images.length);
            }}
            aria-label="Previous picture"
            data-testid="lightbox-prev-button"
            className="absolute left-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:border-[#E74C3C] hover:text-[#E74C3C]"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onIndexChange((index + 1) % images.length);
            }}
            aria-label="Next picture"
            data-testid="lightbox-next-button"
            className="absolute right-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:border-[#E74C3C] hover:text-[#E74C3C]"
          >
            <ChevronRight size={18} />
          </button>
        </>
      )}

      <motion.img
        key={images[index]}
        src={assetUrl(images[index])}
        alt={title ? `${title} — picture ${index + 1}` : `Event picture ${index + 1}`}
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[86vh] max-w-[92vw] border border-white/20 object-contain"
      />

      {images.length > 1 && (
        <p className="absolute bottom-5 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.3em] text-white/60">
          {index + 1} / {images.length}
        </p>
      )}
    </motion.div>
  );
};
